import {
  findRewardBenefitFromHydratedCartEntry,
  getCartEntryId,
  getHydratedEntityId,
} from 'features/cart/utils/minimal-cart';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { EntryTypeEnum, IPriceDto } from 'generated/graphql-gateway';
import { ILocalizedMenuEntry, IndexedMenu, PosServiceMode } from 'state/unified-menu/types';
import {
  getEntryPriceOrZero,
  getFirstDefaultId,
  getPickerProduct,
  isCombo,
  isEntryAvailable,
  isItem,
} from 'state/unified-menu/utils';
import { isCartEntryComboSlot } from 'utils/cart/is-cart-type';
import { CartEntryType } from 'utils/cart/types';

import { ModifierType } from '../product-detail/modifier/types';
import { getModifierMultiplierPrice, mapModifier } from '../product-detail/modifier/utils';
import { ComboSlotType } from '../product-detail/product-customization/types';
import {
  getComboSlotType,
  isCustomizableEntry,
} from '../product-detail/product-customization/utils';

import {
  DEFAULT_SLOT_KEY,
  FlowStep,
  ModifiersSelections,
  PickerSelections,
  ProductWizardStep,
  SlotSelections,
  SlotsSelections,
} from './types';

export function computeSelectedProduct(
  menu: IndexedMenu<ILocalizedMenuEntry>,
  item: ILocalizedMenuEntry,
  posServiceMode: PosServiceMode,
  pickerSelections: Record<string, string> | undefined,
  editingCartEntry?: ILegacyCartEntry,
  pickerAspectKey?: string
): ILocalizedMenuEntry {
  if (editingCartEntry) {
    const editingProduct = menu.find(getCartEntryId(editingCartEntry));

    // @TODO: we should consider updating this in the future for picker rewards
    if (editingCartEntry.type === CartEntryType.offerCombo) {
      return item;
    }

    if (editingProduct && (isCombo(editingProduct) || isItem(editingProduct))) {
      return editingProduct;
    }
  }

  if (isEntryAvailable(item, posServiceMode) && (isItem(item) || isCombo(item))) {
    return item;
  }

  return getPickerProduct(menu, item, pickerSelections ?? {}, posServiceMode, pickerAspectKey);
}

export function initializeSlotSelections(
  selectedProduct: ILocalizedMenuEntry,
  menu: IndexedMenu<ILocalizedMenuEntry>,
  isComboFlow: boolean,
  posServiceMode: PosServiceMode
): SlotsSelections {
  const slotSelections: SlotsSelections = {};

  if (isItem(selectedProduct)) {
    slotSelections[DEFAULT_SLOT_KEY] = {
      itemId: selectedProduct.id,
      modifiers: initializeSlotModifiers(
        menu,
        selectedProduct.id,
        posServiceMode,
        true,
        isComboFlow
      ),
    };
  } else if (isCombo(selectedProduct)) {
    for (const comboSlotRef of selectedProduct.options?.entries ?? []) {
      const comboSlot = menu.get(comboSlotRef.entryId, { type: EntryTypeEnum.COMBO_SLOT });
      const singleOption = comboSlot.options?.entries?.length === 1;
      if (singleOption || isComboFlow) {
        const itemId = getFirstDefaultId(comboSlot);
        slotSelections[comboSlot.id] = {
          itemId,
          modifiers: initializeSlotModifiers(menu, itemId, posServiceMode, true, isComboFlow),
        };
      }
    }
  }

  return slotSelections;
}

export function initializeSlotSelectionsFromCartEntry(
  selectedProduct: ILocalizedMenuEntry,
  menu: IndexedMenu<ILocalizedMenuEntry>,
  editingCartEntry: ILegacyCartEntry
): SlotsSelections {
  const slotSelections: SlotsSelections = {};

  if (isItem(selectedProduct)) {
    let itemCartEntry = editingCartEntry;
    const rewardBenefit = findRewardBenefitFromHydratedCartEntry(editingCartEntry);
    if (rewardBenefit) {
      itemCartEntry = rewardBenefit;
    }

    slotSelections[DEFAULT_SLOT_KEY] = {
      itemId: selectedProduct.id,
      modifiers: getModifiersSelectionsFromCartEntry(itemCartEntry),
    };
    return slotSelections;
  }

  if (!isCombo(selectedProduct)) {
    return slotSelections;
  }

  for (const comboSlotRef of selectedProduct.options?.entries ?? []) {
    const comboSlot = menu.get(comboSlotRef.entryId, { type: EntryTypeEnum.COMBO_SLOT });
    const isSingleOption = comboSlot.options?.entries?.length === 1;
    if (isSingleOption) {
      const itemId = getFirstDefaultId(comboSlot);
      const itemCartEntry = findItemInCartEntries(editingCartEntry, comboSlot.id, itemId);
      slotSelections[comboSlot.id] = {
        itemId,
        modifiers: itemCartEntry ? getModifiersSelectionsFromCartEntry(itemCartEntry) : {},
      };
    } else {
      const editingSlot = editingCartEntry.children.find(
        cartEntry =>
          isCartEntryComboSlot(cartEntry) && comboSlot.id.endsWith(getCartEntryId(cartEntry))
      );
      if (editingSlot) {
        slotSelections[comboSlot.id] = {
          itemId: getCartEntryId(editingSlot.children[0]),
          modifiers: getModifiersSelectionsFromCartEntry(editingSlot.children[0]),
        };
      }
    }
  }

  return slotSelections;
}

/**
 * Initialize the slot modifiers for a given item in the menu.
 *
 * Cases handled:
 * - Picker Meals with modifiers upcharges (Whopper with Bacon)
 * - Offers with required modifiers ($30 Ultimate Bundle - the slot with 16pc chicken nuggets has a required dipping sauce)
 * - Switching between items in a combo (Whopper Meals Sides that have dipping sauces)
 *
 */
export function initializeSlotModifiers(
  menu: IndexedMenu<ILocalizedMenuEntry>,
  itemId: string,
  posServiceMode: PosServiceMode,
  onlyUpcharges = false,
  isComboFlow: boolean
): ModifiersSelections {
  const modifiersSelections: ModifiersSelections = {};

  const item = menu.get(itemId);
  const modifiersCount = item.options?.entries?.length ?? 0;
  for (const { entryId: modifierId, quantity: modifierQuantity } of item.options?.entries ?? []) {
    const modifier = menu.get(modifierId);
    const defaultMultiplierId = getFirstDefaultId(modifier);

    const isUpcharge =
      getModifierMultiplierPrice(modifier, defaultMultiplierId, posServiceMode) > 0;

    const requiredQuantity = modifierQuantity?.def ?? modifierQuantity?.min ?? 0;
    const available = !(modifier.availability && modifier.availability[posServiceMode] === false);
    const isRequiredModifier =
      isComboFlow &&
      available &&
      requiredQuantity > 0 &&
      mapModifier(menu, modifierId, modifiersCount, posServiceMode).type !== ModifierType.CHECKBOX;

    if (onlyUpcharges && !isUpcharge && !isRequiredModifier) {
      continue;
    }

    const multiplierRef = modifier.options?.entries?.find(
      x =>
        x.entryId === defaultMultiplierId &&
        !(x.availability && x.availability[posServiceMode] === false)
    );
    if (multiplierRef) {
      modifiersSelections[modifier.id] = {};
      modifiersSelections[modifier.id][defaultMultiplierId] = requiredQuantity ?? 1;
    }
  }

  return modifiersSelections;
}

function findItemInCartEntries(
  cartEntry: ILegacyCartEntry,
  comboSlotId: string,
  itemId: string
): ILegacyCartEntry | undefined {
  const matchingDirectItem = cartEntry.children.find(x => getCartEntryId(x) === itemId);
  if (matchingDirectItem) {
    return matchingDirectItem;
  }
  const matchingComboSlot = cartEntry.children.find(x => comboSlotId.endsWith(getCartEntryId(x)));

  return matchingComboSlot?.children.find(x => getCartEntryId(x) === itemId);
}

function getModifiersSelectionsFromCartEntry(itemCartEntry: ILegacyCartEntry): ModifiersSelections {
  const modifiersSelections: ModifiersSelections = {};

  itemCartEntry.children.forEach(modifierCartEntry => {
    modifierCartEntry.children.forEach(modifierMultiplierCartEntry => {
      const modifierMultiplierKey = getCartEntryId(modifierMultiplierCartEntry);
      if (modifierMultiplierKey) {
        const slotKey = getHydratedEntityId(
          getCartEntryId(modifierCartEntry),
          getCartEntryId(itemCartEntry)
        );
        if (!modifiersSelections[slotKey]) {
          modifiersSelections[slotKey] = {};
        }
        modifiersSelections[slotKey][modifierMultiplierKey] = modifierMultiplierCartEntry.quantity;
      }
    });
  });

  return modifiersSelections;
}

// function computeComboSlotCalories(
//   menu: IndexedMenu,
//   comboSlot: MenuEntry,
//   selection: SlotSelections | undefined
// ): number {
//   const entry = selection
//     ? menu.get(selection.itemId)
//     : menu.get(getFirstDefaultId(comboSlot, true));

//   const calories = entry.nutrition?.calories?.def ?? 0;
//   return calories * (entry.options?.quantity?.default ?? 0);
// }

// export function computeComboCalories(
//   menu: IndexedMenu,
//   combo: MenuEntry,
//   selection: SlotsSelections
// ) {
//   let calories = 0;
//   for (const comboSlotRef of combo.options?.entries ?? []) {
//     calories += computeComboSlotCalories(
//       menu,
//       menu.get(comboSlotRef.entryId),
//       selection[comboSlotRef.entryId]
//     );
//   }
//   return calories;
// }

function computeComboSlotSodium(
  menu: IndexedMenu<ILocalizedMenuEntry>,
  comboSlot: ILocalizedMenuEntry,
  selection: SlotSelections | undefined
): number {
  const entry = selection
    ? menu.get(selection.itemId)
    : menu.get(getFirstDefaultId(comboSlot, true));

  return entry.nutrition?.nutrients?.sodium ?? 0;
}

export function computeComboSodium(
  menu: IndexedMenu<ILocalizedMenuEntry>,
  combo: ILocalizedMenuEntry,
  selection: SlotsSelections
): number {
  let sodium = 0;
  for (const comboSlotRef of combo.options?.entries ?? []) {
    sodium += computeComboSlotSodium(
      menu,
      menu.get(comboSlotRef.entryId),
      selection[comboSlotRef.entryId]
    );
  }

  return sodium;
}

export function computeMenuItemPrice(
  menu: IndexedMenu<ILocalizedMenuEntry>,
  entry: ILocalizedMenuEntry,
  serviceMode: PosServiceMode,
  itemContextualPrice: IPriceDto | undefined,
  selections: ModifiersSelections
): number {
  if (!isItem(entry)) {
    throw new Error(`Entry ${entry.id} is not an item`);
  }

  let price = getEntryPriceOrZero(entry, serviceMode, itemContextualPrice);

  for (const [modifierKey, multipliers] of Object.entries(selections)) {
    const modifier = menu.get(modifierKey);
    for (const [multiplierId, quantity] of Object.entries(multipliers)) {
      price += getModifierMultiplierPrice(modifier, multiplierId, serviceMode) * quantity;
    }
  }

  return price;
}

export function buildFlow(
  menuObject: ILocalizedMenuEntry,
  menu: IndexedMenu<ILocalizedMenuEntry>,
  pickerSelections: PickerSelections,
  posServiceMode: PosServiceMode
): FlowStep[] {
  const flow: FlowStep[] = [];

  if (menuObject.type !== EntryTypeEnum.PICKER) {
    return flow;
  }

  // Step 1: Add the ProductHome step
  const firstPickerAspectId = getFirstDefaultId(menuObject);
  flow.push({
    step: ProductWizardStep.ProductHome,
    pickerAspectKey: firstPickerAspectId,
  });

  // Step 2: Traverse Picker Aspects
  let currentPickerAspectId = firstPickerAspectId;
  let selectedProduct;
  while (currentPickerAspectId) {
    let selectedAspectOptionId = pickerSelections[currentPickerAspectId];
    if (!selectedAspectOptionId) {
      selectedAspectOptionId = getFirstDefaultId(
        menu.get(currentPickerAspectId, { type: EntryTypeEnum.PICKER_ASPECT })
      );
    }
    const selectedAspectOption = menu.get(selectedAspectOptionId, {
      type: EntryTypeEnum.ASPECT_OPTION,
    });
    const nextEntityId = getFirstDefaultId(selectedAspectOption);
    const nextEntity = menu.get(nextEntityId);
    if (nextEntity.type !== EntryTypeEnum.PICKER_ASPECT) {
      selectedProduct = nextEntity;
      break;
    }

    flow.push({
      step: ProductWizardStep.PickerSelection,
      pickerAspectKey: nextEntityId,
    });
    currentPickerAspectId = nextEntityId;
  }

  // Step 3.A: When Combo => Traverse Combo Slots
  if (selectedProduct && isCombo(selectedProduct)) {
    for (const slot of selectedProduct.options?.entries || []) {
      const comboSlotMenuEntry = menu.get(slot.entryId, { type: EntryTypeEnum.COMBO_SLOT });
      if (!isCustomizableEntry(comboSlotMenuEntry, menu, posServiceMode)) {
        continue;
      }

      const comboSlotType: ComboSlotType = getComboSlotType(menu, slot.entryId!);
      const step: ProductWizardStep =
        comboSlotType === ComboSlotType.SIDES
          ? ProductWizardStep.PickerCustomizationSides
          : comboSlotType === ComboSlotType.DRINKS
          ? ProductWizardStep.PickerCustomizationDrinks
          : ProductWizardStep.PickerCustomization;
      flow.push({ step, slotKey: slot.entryId });
    }
  }

  // Step 3.B: When Item => Add mainItem customization
  if (
    selectedProduct &&
    isItem(selectedProduct) &&
    isCustomizableEntry(selectedProduct, menu, posServiceMode)
  ) {
    flow.push({
      step: ProductWizardStep.PickerCustomization,
      slotKey: DEFAULT_SLOT_KEY,
    });
  }

  // Step 4: Add the PickerReview step
  flow.push({
    step: ProductWizardStep.PickerReview,
  });

  // Nullify invalid flows (only Home and Review steps)
  if (flow.length === 2) {
    return [];
  }

  return flow;
}
